// frontend/src/hooks/useQuickPhrases.js
/**
 * Hook för ;-triggade fraser (fraslexikon) i textrutor.
 *
 * Skriv `;` (i början av text eller efter mellanslag/radbrytning) följt av
 * triggerordet -> en lista med matchande fraser visas. Enter/Tab infogar
 * vald fras, piltangenter väljer, Esc eller mellanslag stänger.
 *
 * Usage:
 * const {
 *   phrases,
 *   showDropdown,
 *   dropdownPosition,
 *   searchQuery,
 *   filteredPhrases,
 *   selectedIndex,
 *   handleKeyDown,
 *   handleInput,
 *   selectPhrase,
 *   closeDropdown
 * } = useQuickPhrases(textareaRef, onInsertPhrase);
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getPhrases, PHRASES_CHANGED_EVENT } from '../components/JournalEditor/api/journalApi';

// Delad cache: en journal har många sektioner (en hook per textruta) —
// lexikonet hämtas en gång och laddas om när det ändras.
let phrasesCache = null;
let phrasesRequest = null;
let phrasesGeneration = 0; // ökas vid ändring så att en pågående, inaktuell hämtning inte cachas

function sortPhrases(list) {
  return [...list].sort((a, b) => a.title.localeCompare(b.title, 'sv'));
}

function fetchPhrases() {
  if (phrasesCache) return Promise.resolve(phrasesCache);
  if (!phrasesRequest) {
    const generation = phrasesGeneration;
    const request = getPhrases()
      .then(data => {
        const sorted = sortPhrases(data.personal_phrases || []);
        if (generation === phrasesGeneration) phrasesCache = sorted;
        return sorted;
      })
      .finally(() => {
        if (phrasesRequest === request) phrasesRequest = null;
      });
    phrasesRequest = request;
  }
  return phrasesRequest;
}

// Nollställ cachen (anropas när lexikonet ändrats); nästa hämtning går mot API:t
window.addEventListener(PHRASES_CHANGED_EVENT, () => {
  phrasesGeneration += 1;
  phrasesCache = null;
  phrasesRequest = null;
});

export function useQuickPhrases(textareaRef, onTextChange) {
  // All phrases loaded from API
  const [phrases, setPhrases] = useState(phrasesCache || []);
  const [loading, setLoading] = useState(!phrasesCache);

  // Autocomplete state
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [triggerPosition, setTriggerPosition] = useState(null); // Position of ; character

  // Track if we're in autocomplete mode
  const isAutocompleteActive = useRef(false);
  // Har användaren valt i listan med piltangenterna? (styr Enter utan sökord)
  const hasNavigated = useRef(false);

  const loadPhrases = useCallback(async () => {
    try {
      setLoading(true);
      setPhrases(await fetchPhrases());
    } catch (error) {
      console.error('Kunde inte ladda fraslexikon:', error);
      setPhrases([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Ladda vid mount + ladda om när lexikonet ändrats (Fraslexikon/import)
  useEffect(() => {
    loadPhrases();
    window.addEventListener(PHRASES_CHANGED_EVENT, loadPhrases);
    return () => window.removeEventListener(PHRASES_CHANGED_EVENT, loadPhrases);
  }, [loadPhrases]);

  // Filtrera: triggerord som börjar på sökordet först, sedan övriga träffar
  const filteredPhrases = useMemo(() => {
    const query = searchQuery.toLowerCase();
    if (!query) return phrases;
    return [
      ...phrases.filter(p => p.title.toLowerCase().startsWith(query)),
      ...phrases.filter(p => {
        const title = p.title.toLowerCase();
        return !title.startsWith(query) && title.includes(query);
      })
    ];
  }, [phrases, searchQuery]);

  // Calculate dropdown position based on cursor
  const calculateDropdownPosition = useCallback(() => {
    if (!textareaRef?.current) return { top: 0, left: 0 };

    const textarea = textareaRef.current;
    const rect = textarea.getBoundingClientRect();
    const computed = window.getComputedStyle(textarea);

    // Get cursor position in text
    const cursorPos = textarea.selectionStart;
    const textBeforeCursor = textarea.value.substring(0, cursorPos);

    // Get the last line
    const lines = textBeforeCursor.split('\n');
    const lastLine = lines[lines.length - 1];

    // Calculate approximate position
    const lineHeight = parseInt(computed.lineHeight) || parseInt(computed.fontSize) * 1.2;
    const charWidth = parseInt(computed.fontSize) * 0.6; // Approximate

    const top = rect.top + (lines.length * lineHeight) - textarea.scrollTop + 24;
    const left = rect.left + (lastLine.length * charWidth);

    return {
      top: Math.min(top, window.innerHeight - 250),
      left: Math.min(left, window.innerWidth - 320)
    };
  }, [textareaRef]);

  // Close dropdown and reset state
  const closeDropdown = useCallback(() => {
    setShowDropdown(false);
    setSearchQuery('');
    setSelectedIndex(0);
    setTriggerPosition(null);
    isAutocompleteActive.current = false;
    hasNavigated.current = false;
  }, []);

  // Handle input changes - detect ; trigger
  const handleInput = useCallback((e) => {
    const textarea = e.target;
    const value = textarea.value;
    const cursorPos = textarea.selectionStart;

    // `;` startar bara en fras i början av texten eller efter blanksteg,
    // så att semikolon mitt i en mening inte öppnar listan
    const charBefore = cursorPos >= 2 ? value[cursorPos - 2] : '';
    if (
      value[cursorPos - 1] === ';' &&
      !isAutocompleteActive.current &&
      (charBefore === '' || /\s/.test(charBefore))
    ) {
      // Start autocomplete
      isAutocompleteActive.current = true;
      hasNavigated.current = false;
      setTriggerPosition(cursorPos - 1);
      setSearchQuery('');
      setSelectedIndex(0);
      setDropdownPosition(calculateDropdownPosition());
      setShowDropdown(true);
      return;
    }

    // If autocomplete is active, update search query
    if (isAutocompleteActive.current && triggerPosition !== null) {
      // `;` raderat eller markören flyttad före triggern
      if (cursorPos <= triggerPosition || value[triggerPosition] !== ';') {
        closeDropdown();
        return;
      }

      // Get text after ; up to cursor
      const textAfterTrigger = value.substring(triggerPosition + 1, cursorPos);

      // Check if user typed space or moved away from trigger
      if (textAfterTrigger.includes(' ') || textAfterTrigger.includes('\n')) {
        closeDropdown();
        return;
      }

      setSearchQuery(textAfterTrigger);
      setSelectedIndex(0);
      hasNavigated.current = false;
    }
  }, [triggerPosition, calculateDropdownPosition, closeDropdown]);

  // Select a phrase and insert it
  const selectPhrase = useCallback((phrase) => {
    if (!textareaRef?.current || triggerPosition === null) return;

    const textarea = textareaRef.current;
    const value = textarea.value;

    // Ersätt exakt `;` + sökordet — aldrig fram till markören, som kan ha flyttats
    const triggerEnd = triggerPosition + 1 + searchQuery.length;
    if (value.substring(triggerPosition, triggerEnd) !== ';' + searchQuery) {
      closeDropdown();
      return;
    }
    const before = value.substring(0, triggerPosition);
    const after = value.substring(triggerEnd);
    const newValue = before + phrase.phrase_text + after;

    // Update textarea value
    if (onTextChange) {
      onTextChange(newValue);
    }

    // Set cursor position after inserted text
    const newCursorPos = triggerPosition + phrase.phrase_text.length;
    setTimeout(() => {
      textarea.selectionStart = newCursorPos;
      textarea.selectionEnd = newCursorPos;
      textarea.focus();
    }, 0);

    closeDropdown();
  }, [textareaRef, triggerPosition, searchQuery, onTextChange, closeDropdown]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!showDropdown) return false;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        hasNavigated.current = true;
        setSelectedIndex(prev =>
          prev < filteredPhrases.length - 1 ? prev + 1 : prev
        );
        return true;

      case 'ArrowUp':
        e.preventDefault();
        hasNavigated.current = true;
        setSelectedIndex(prev => prev > 0 ? prev - 1 : 0);
        return true;

      case 'Enter':
      case 'Tab':
        // Bara `;` + Enter ska ge en vanlig radbrytning, inte infoga första frasen
        if (filteredPhrases.length > 0 && (searchQuery || hasNavigated.current)) {
          e.preventDefault();
          selectPhrase(filteredPhrases[selectedIndex] || filteredPhrases[0]);
          return true;
        }
        closeDropdown();
        break;

      case 'Escape':
        e.preventDefault();
        closeDropdown();
        return true;

      // Markören lämnar triggern -> stäng (annars kan fel text ersättas)
      case 'ArrowLeft':
      case 'ArrowRight':
      case 'Home':
      case 'End':
      case 'PageUp':
      case 'PageDown':
        closeDropdown();
        break;

      default:
        break;
    }

    return false;
  }, [showDropdown, filteredPhrases, selectedIndex, searchQuery, selectPhrase, closeDropdown]);

  // Stäng vid klick utanför listan — även i textrutan, eftersom klicket flyttar markören
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showDropdown && !e.target.closest?.('.quick-phrase-dropdown')) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown, closeDropdown]);

  return {
    phrases,
    loading,
    showDropdown,
    dropdownPosition,
    searchQuery,
    filteredPhrases,
    selectedIndex,
    handleKeyDown,
    handleInput,
    selectPhrase,
    closeDropdown,
    refreshPhrases: loadPhrases
  };
}

export default useQuickPhrases;
